// ============================================================
// PREZENCE — Consent Gate
// On load, checks whether the logged-in user has consented to the
// current monitoring policy. If not, shows a clear, employee-friendly
// consent screen they must acknowledge before using the app.
// Wraps the app content in AppLayout.
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import toast from 'react-hot-toast';
import './consent.css';

export default function ConsentGate({ children }) {
  const [checking, setChecking] = useState(true);
  const [needsConsent, setNeedsConsent] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const check = useCallback(async () => {
    try {
      const { data } = await api.get('/consent/status');
      if (data.needsConsent) {
        const p = await api.get('/consent/policy');
        setPolicy(p.data.policy);
        setNeedsConsent(true);
      }
    } catch (e) {
      // If the check fails (e.g. backend not updated yet), don't block the app.
      setNeedsConsent(false);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  const accept = async () => {
    setSubmitting(true);
    try {
      await api.post('/consent/accept');
      setNeedsConsent(false);
      toast.success('Thank you — you can now use Prezence.');
    } catch (e) {
      toast.error('Could not record your response. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) return null; // brief; avoids a flash of the app before the check

  if (needsConsent && policy) {
    return (
      <div className="consent-overlay">
        <div className="consent-card">
          <div className="consent-head">
            <div className="consent-badge">🔒</div>
            <div>
              <h1 className="consent-title">Before you start</h1>
              <p className="consent-sub">How Prezence works, and what it means for you</p>
            </div>
          </div>

          <p className="consent-intro">{policy.intro}</p>

          <div className="consent-section-label">WHAT IS COLLECTED</div>
          <div className="consent-items">
            {policy.items.map((it, i) => (
              <div className="consent-item" key={i}>
                <div className="consent-item-title">{it.title}</div>
                <div className="consent-item-detail">{it.detail}</div>
              </div>
            ))}
          </div>

          <div className="consent-section-label">YOUR PROTECTIONS</div>
          <ul className="consent-assurances">
            {policy.assurances.map((a, i) => <li key={i}>{a}</li>)}
          </ul>

          <div className="consent-actions">
            <button className="consent-accept" onClick={accept} disabled={submitting}>
              {submitting ? 'Recording…' : 'I understand and acknowledge'}
            </button>
            <p className="consent-foot">
              Version {policy.version}. Your acknowledgment is recorded securely. If you have
              questions, please speak with your HR team.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return children;
}
